/**
 * 文件说明：统一派生节点顶部工具条状态和按钮能力。
 *
 * 这里只判断“节点当前应暴露哪些顶部能力”，不渲染具体按钮，避免各 Toolbar 重复定义空态、工作态和结果态。
 */
import { useMemo } from 'react'
import { MATERIAL_OP } from '@/shared/lib/op-registry'
import type { BaseNodeData } from '@/shared/lib/node-registry'
import { useCustomNodeData } from './useCustomNodeData'
import { useNodeContextPayloads } from './node-context'

export type NodeToolbarContentState = 'blank' | 'working' | 'result' | 'material'

export interface NodeToolbarCapabilities {
  state: NodeToolbarContentState
  canDownload: boolean
  canFocus: boolean
  canOpenMoreOps: boolean
  canPreview: boolean
  canShowHistory: boolean
  canUpload: boolean
}

interface ResolveNodeToolbarCapabilitiesArgs {
  hasConfiguredInput?: boolean
  hasContext: boolean
  hasResult: boolean
  nodeData: BaseNodeData
  nodeType: string
}

interface UseNodeToolbarCapabilitiesOptions {
  hasConfiguredInput?: boolean
  hasResult: boolean
  nodeId: string
  nodeType: string
}

/**
 * 判断节点是否处在任务相关状态。
 *
 * @param nodeData 节点自定义数据快照
 * @returns 当前节点是否有任务状态
 */
function hasTaskState(nodeData: BaseNodeData): boolean {
  return (
    nodeData.status === 'pending' ||
    nodeData.status === 'running' ||
    nodeData.status === 'failed' ||
    nodeData.status === 'canceled'
  )
}

/**
 * 判断空态节点是否允许显示顶部上传入口。
 *
 * @param nodeType 当前节点类型
 * @param op 当前节点 OP
 * @returns 是否属于允许 Blank 上传的基础生成节点
 */
function canBlankNodeUpload(nodeType: string, op: unknown): boolean {
  const normalizedOp = typeof op === 'string' ? op.trim() : ''
  const uploadableBlankOps: Record<string, string> = {
    image: 'text2image',
    gallery: 'text2image',
    video: 'text2video',
    audio: 'text2audio',
    'audio-list': 'text2audio',
    'text-list': 'text-llm',
  }

  return uploadableBlankOps[nodeType] === normalizedOp
}

/**
 * 派生工具条内容状态。
 *
 * @param args 节点结果、上下文和配置输入信息
 * @returns 节点顶部工具条的统一内容状态
 */
function resolveNodeToolbarContentState({
  hasConfiguredInput,
  hasContext,
  hasResult,
  nodeData,
}: ResolveNodeToolbarCapabilitiesArgs): NodeToolbarContentState {
  if (nodeData.op === MATERIAL_OP) {
    return 'material'
  }

  if (hasResult) {
    return 'result'
  }

  if (hasContext || hasConfiguredInput || hasTaskState(nodeData)) {
    return 'working'
  }

  return 'blank'
}

/**
 * 根据节点状态派生顶部按钮能力。
 *
 * @param args 节点结果、上下文和配置输入信息
 * @returns 各类顶部按钮是否应显示
 */
export function resolveNodeToolbarCapabilities(args: ResolveNodeToolbarCapabilitiesArgs): NodeToolbarCapabilities {
  const state = resolveNodeToolbarContentState(args)
  const hasResult = args.hasResult
  const isMaterial = state === 'material'

  return {
    state,
    canDownload: hasResult,
    canFocus: state !== 'blank',
    canOpenMoreOps: state === 'blank' || state === 'working' || state === 'result' || state === 'material',
    canPreview: hasResult,
    canShowHistory: !isMaterial && hasResult,
    canUpload: state === 'blank' && canBlankNodeUpload(args.nodeType, args.nodeData.op),
  }
}

/**
 * 读取上下文并派生当前节点顶部工具条能力。
 *
 * @param options 当前节点 ID、结果状态和额外配置输入
 * @returns 当前节点顶部工具条能力
 */
export function useNodeToolbarCapabilities({
  hasConfiguredInput = false,
  hasResult,
  nodeId,
  nodeType,
}: UseNodeToolbarCapabilitiesOptions): NodeToolbarCapabilities {
  const { snapshot } = useCustomNodeData<BaseNodeData>(nodeId)
  const contextPayloads = useNodeContextPayloads(nodeId)

  return useMemo(
    () => resolveNodeToolbarCapabilities({
      hasConfiguredInput,
      hasContext: contextPayloads.items.length > 0,
      hasResult,
      nodeData: snapshot,
      nodeType,
    }),
    [contextPayloads.items.length, hasConfiguredInput, hasResult, nodeType, snapshot],
  )
}
